/**
 * Red za mutacije koje padnu dok je backend „uspan” (free tier sleep, cold start,
 * 502/503/524, mreža). Ponavlja se nakon što `/health` odgovori — vidi
 * `flushApiWakeQueue` i pozive iz AuthContext / Layout.
 *
 * SEC-02: svaka stavka nosi vlasnika (company_id:user_id:verzija-prava) i šalje se
 * samo dok je ista sesija aktivna. Stavke bez vlasnika (starije verzije) su u karanteni.
 * U red ulaze samo operacije iz kataloga sigurnih ponavljanja (vidi WAKE_QUEUE_CATALOG).
 */

import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { isGlobalAdminSession } from './actingCompany';
import { isSnapshotCurrent, scopeSnapshot, ScopeChangedError } from './sessionScope';

const LEGACY_KEY = 'zoma_api_wake_queue_v1';
const STORAGE_KEY = 'zoma_api_wake_queue_v2';
const MAX_ITEMS = 45;
const MAX_JSON_CHARS = 100_000;

/**
 * pending: čeka automatsko ponavljanje; failed: poslužitelj je trajno odbio (dead-letter,
 * čeka ručno „Pokušaj ponovno / Odbaci”); quarantined: nikad se ne šalje automatski.
 */
export type WakeQueueStatus = 'pending' | 'failed' | 'quarantined';

export type QueuedItem = {
  id: string;
  method: string;
  url: string;
  data: unknown;
  createdAt: string;
  /** company_id:user_id:verzija-prava; null = stavka iz starije verzije bez vlasnika. */
  owner: string | null;
  /** Ključ operacije iz kataloga; null ako je stavka naslijeđena. */
  operation: string | null;
  status: WakeQueueStatus;
  attempts: number;
  lastAttemptAt?: string;
  httpStatus?: number;
  reason?: string;
  /** Posao 15D: Idempotency-Key prvog slanja; isti se šalje kod svakog ponovnog pokušaja. */
  idempotencyKey?: string;
};

type LegacyItem = { id: string; method: string; url: string; data: unknown; createdAt: string; error?: string };

/**
 * Operacije koje se smiju automatski ponoviti: idempotentne izmjene (PUT cijelog
 * zapisa) i brisanja po ID-ju. POST (stvaranje, prijelazi stanja), administracija,
 * korisnici, uloge i autentifikacija nikad ne ulaze u opći red.
 */
export const WAKE_QUEUE_CATALOG: { key: string; method: string; pattern: RegExp; label: string }[] = [
  { key: 'location.update', method: 'PUT', pattern: /^\/locations\/[^/]+$/, label: 'Izmjena lokacije' },
  { key: 'crm_client.update', method: 'PUT', pattern: /^\/crm\/clients\/[^/]+$/, label: 'Izmjena CRM klijenta' },
  { key: 'crm_client_location.link', method: 'PUT', pattern: /^\/crm\/clients\/[^/]+\/locations\/[^/]+$/, label: 'Povezivanje lokacije s klijentom' },
  { key: 'crm_client_location.unlink', method: 'DELETE', pattern: /^\/crm\/clients\/[^/]+\/locations\/[^/]+$/, label: 'Odvajanje lokacije od klijenta' },
  { key: 'crm_contact.update', method: 'PUT', pattern: /^\/crm\/contacts\/[^/]+$/, label: 'Izmjena CRM kontakta' },
  { key: 'crm_contact.delete', method: 'DELETE', pattern: /^\/crm\/contacts\/[^/]+$/, label: 'Brisanje CRM kontakta' },
  { key: 'crm_note.delete', method: 'DELETE', pattern: /^\/crm\/notes\/[^/]+$/, label: 'Brisanje CRM bilješke' },
  // Posao 25-2: izmjena i zatvaranje zadatka su idempotentni na backendu; POST (novi zadatak) ne ide u red.
  { key: 'crm_task.update', method: 'PUT', pattern: /^\/crm\/tasks\/[^/]+$/, label: 'Izmjena CRM zadatka' },
  { key: 'crm_task.status', method: 'PUT', pattern: /^\/crm\/tasks\/[^/]+\/status$/, label: 'Zatvaranje CRM zadatka' },
  // Posao 25-3: izmjena i akcije ugovora su idempotentne na backendu; POST (novi ugovor, obnova, plan) ne ide u red.
  { key: 'crm_contract.update', method: 'PUT', pattern: /^\/crm\/contracts\/[^/]+$/, label: 'Izmjena ugovora' },
  { key: 'crm_contract.delete', method: 'DELETE', pattern: /^\/crm\/contracts\/[^/]+$/, label: 'Brisanje nacrta ugovora' },
  { key: 'crm_contract.action', method: 'PUT', pattern: /^\/crm\/contracts\/[^/]+\/(activate|cancel|archive|approve-renewal)$/, label: 'Promjena statusa ugovora' },
  { key: 'crm_contract_job.link', method: 'PUT', pattern: /^\/crm\/contracts\/[^/]+\/recurring-jobs\/[^/]+$/, label: 'Povezivanje posla s ugovorom' },
  { key: 'crm_contract_job.unlink', method: 'DELETE', pattern: /^\/crm\/contracts\/[^/]+\/recurring-jobs\/[^/]+$/, label: 'Odvajanje posla od ugovora' },
  { key: 'vehicle.update', method: 'PUT', pattern: /^\/vehicles\/[^/]+$/, label: 'Izmjena vozila' },
  { key: 'route.update', method: 'PUT', pattern: /^\/routes\/[^/]+$/, label: 'Izmjena rute' },
  { key: 'route_location.delete', method: 'DELETE', pattern: /^\/routes\/[^/]+\/locations\/[^/]+$/, label: 'Uklanjanje točke s rute' },
  { key: 'visit_reason.update', method: 'PUT', pattern: /^\/visit-reasons\/[^/]+$/, label: 'Izmjena razloga obilaska' },
  { key: 'task_template.update', method: 'PUT', pattern: /^\/tasks\/templates\/[^/]+$/, label: 'Izmjena predloška zadatka' },
];

function pathOnly(url: string): string {
  return url.split('?')[0].replace(/^\/api(?=\/)/, '');
}

export function matchWakeOperation(method: string, url: string) {
  const m = method.toUpperCase();
  const path = pathOnly(url);
  return WAKE_QUEUE_CATALOG.find(op => op.method === m && op.pattern.test(path)) || null;
}

export function wakeOperationLabel(item: Pick<QueuedItem, 'operation' | 'method'>): string {
  return WAKE_QUEUE_CATALOG.find(op => op.key === item.operation)?.label || `Zahtjev iz starije verzije (${item.method})`;
}

function readJson(key: string): unknown[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function writeJson(key: string, items: unknown[]) {
  try {
    if (items.length) localStorage.setItem(key, JSON.stringify(items));
    else localStorage.removeItem(key);
  } catch {
    /* quota */
  }
}

function isTimeMutation(url: string) { url = pathOnly(url); return ['/gps/start-work', '/gps/end-work', '/visits/start', '/visits/sync'].includes(url) || /^\/visits\/[^/]+\/complete$/.test(url) || /^\/time\//.test(url); }

const NOTICE_KEY = 'zoma_wake_queue_sensitive_removed_v1';
const SENSITIVE_PATH = /(^|\/)(auth|password|passwords|reset-password|forgot-password|verify|tokens?|2fa|mfa|otp|security|role-management|admin)(\/|$)/i;
const SECRET_KEY = /(^|_)(password|passwd|pwd|secret|token|otp|totp|mfa|apikey|api_key|authorization|credentials?|private_key|pin)($|_)/i;

function hasSecretKeys(value: unknown, depth = 0): boolean {
  if (!value || typeof value !== 'object' || depth > 8) return false;
  if (Array.isArray(value)) return value.some(v => hasSecretKeys(v, depth + 1));
  return Object.keys(value as Record<string, unknown>).some(k => {
    const norm = k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[-\s]/g, '_').toLowerCase();
    return SECRET_KEY.test(norm) || hasSecretKeys((value as Record<string, unknown>)[k], depth + 1);
  });
}

function rawHasSecretKeys(data: unknown): boolean {
  if (typeof data === 'string') {
    try { return hasSecretKeys(JSON.parse(data)); } catch { return /pass|secret|token/i.test(data); }
  }
  return hasSecretKeys(data);
}

/**
 * SEC-03: autentifikacijske i sigurnosne operacije ili tijela s tajnama
 * (lozinke, tokeni, kodovi) nikad se ne spremaju u trajni red.
 */
export function isSensitiveRequest(url: string, data: unknown): boolean {
  return SENSITIVE_PATH.test(pathOnly(url || '')) || rawHasSecretKeys(data);
}

/**
 * Pokretanje: ukloni osjetljive stavke iz starije i nove pohrane. Sadržaj se ne
 * ispisuje; ostaje samo oznaka da korisnik promjenu mora ponoviti online.
 */
export function purgeSensitiveWakeQueueItems(): number {
  let removed = 0;
  for (const key of [LEGACY_KEY, STORAGE_KEY]) {
    const items = readJson(key) as { url?: string; data?: unknown }[];
    const keep = items.filter(item => !isSensitiveRequest(String(item?.url || ''), item?.data));
    if (keep.length !== items.length) {
      removed += items.length - keep.length;
      writeJson(key, keep);
    }
  }
  if (removed) {
    try { localStorage.setItem(NOTICE_KEY, '1'); } catch { /* nedostupno */ }
  }
  return removed;
}

/** Vraća true jednom ako su pri pokretanju uklonjene osjetljive stavke (za poruku korisniku). */
export function consumeSensitiveRemovalNotice(): boolean {
  try {
    if (localStorage.getItem(NOTICE_KEY) !== '1') return false;
    localStorage.removeItem(NOTICE_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Stari vremenski zahtjevi ostaju u v1 ključu za pregled (TimeRecordsPanel); nikad se ne šalju. */
export function getLegacyTimeQueueErrors(): LegacyItem[] { return (readJson(LEGACY_KEY) as LegacyItem[]).filter(item => isTimeMutation(item.url)); }

function newId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `wq-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Posao 15D: zaglavlje i oblik ključa moraju odgovarati backendu (8-128 znakova, A-Z a-z 0-9 - _). */
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

export function isValidIdempotencyKey(value: unknown): value is string {
  return typeof value === 'string' && IDEMPOTENCY_KEY_PATTERN.test(value);
}

export function newIdempotencyKey(): string {
  const rng = typeof crypto !== 'undefined' ? crypto : undefined;
  if (rng?.randomUUID) return rng.randomUUID();
  if (rng?.getRandomValues) {
    const bytes = rng.getRandomValues(new Uint8Array(16));
    return `ik-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')}`;
  }
  return `ik-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function readIdempotencyHeader(config: InternalAxiosRequestConfig | undefined): string | undefined {
  const headers = config?.headers as unknown as { get?: (name: string) => unknown; [key: string]: unknown } | undefined;
  if (!headers) return undefined;
  const value = typeof headers.get === 'function' ? headers.get(IDEMPOTENCY_HEADER) : headers[IDEMPOTENCY_HEADER];
  return isValidIdempotencyKey(value) ? value : undefined;
}

/**
 * Posao 15D: request interceptor. Svaka operacija iz kataloga dobiva ključ PRIJE prvog
 * slanja, pa server prepozna i ponavljanje zahtjeva koji je istekao nakon upisa.
 * Postojeći ključ (ponovno slanje iz reda) se ne mijenja; nova izmjena = nov ključ.
 */
export function attachIdempotencyKey(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (!config || readIdempotencyHeader(config)) return config;
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) return config;
  const path = normalizeRequestUrl(config);
  if (!matchWakeOperation(config.method || 'get', path) || isSensitiveRequest(path, config.data)) return config;
  const key = newIdempotencyKey();
  const headers = config.headers as unknown as { set?: (name: string, value: string) => unknown; [key: string]: unknown };
  if (typeof headers?.set === 'function') headers.set(IDEMPOTENCY_HEADER, key);
  else config.headers = { ...(config.headers || {}), [IDEMPOTENCY_HEADER]: key } as unknown as InternalAxiosRequestConfig['headers'];
  return config;
}

/** Stara stavka bez ključa: koristi `id` ako je valjan ključ, inače nov; sprema se prije slanja. */
function ensureItemKey(item: QueuedItem): string {
  if (isValidIdempotencyKey(item.idempotencyKey)) return item.idempotencyKey;
  return isValidIdempotencyKey(item.id) ? item.id : newIdempotencyKey();
}

/**
 * Prebacuje ne-vremenske stavke iz stare pohrane u karantenu (bez vlasnika, nikad se ne šalju).
 */
export function migrateLegacyWakeQueue(): void {
  purgeSensitiveWakeQueueItems();
  const legacy = readJson(LEGACY_KEY) as LegacyItem[];
  if (!legacy.length) return;
  const keep = legacy.filter(item => isTimeMutation(item.url));
  const moved = legacy.filter(item => !isTimeMutation(item.url));
  if (!moved.length) return;
  const q = readQueue();
  for (const item of moved) {
    q.push({
      id: item.id || newId(),
      method: String(item.method || '').toUpperCase(),
      url: item.url,
      data: item.data,
      createdAt: item.createdAt,
      owner: null,
      operation: null,
      status: 'quarantined',
      attempts: 0,
      reason: 'Zahtjev iz starije verzije nema potvrđenog autora; neće se automatski poslati.',
    });
  }
  writeQueue(q);
  writeJson(LEGACY_KEY, keep);
}

function readQueue(): QueuedItem[] {
  return (readJson(STORAGE_KEY) as QueuedItem[]).filter(x => x && typeof x.id === 'string');
}

function writeQueue(items: QueuedItem[]) {
  writeJson(STORAGE_KEY, items);
  try { window.dispatchEvent(new Event('zoma-wake-queue-changed')); } catch { /* bez prozora */ }
}

function updateItem(id: string, patch: Partial<QueuedItem> | null) {
  const q = readQueue();
  writeQueue(patch ? q.map(x => (x.id === id ? { ...x, ...patch } : x)) : q.filter(x => x.id !== id));
}

/** company_id:user_id bez verzije prava. */
function personOf(owner: string | null): string | null {
  return owner ? owner.split(':').slice(0, 2).join(':') : null;
}

const QUARANTINE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Pokretanje (posao 15B, odluka 4): uklanja stavke drugih korisnika (druga osoba od
 * `owner`) i karantenske stavke starije od 14 dana (ili bez valjanog datuma). Vlastite
 * stavke na čekanju i neuspjele stavke ostaju dok ih korisnik ne riješi. Bez poznatog
 * korisnika briše se samo stara karantena. Sadržaj stavki se ne ispisuje.
 */
export function purgeForeignAndStaleWakeQueueItems(owner: string | null, now: number = Date.now()): number {
  const items = readQueue();
  const me = personOf(owner);
  const keep = items.filter(x => {
    if (x.status === 'quarantined') {
      const created = Date.parse(x.createdAt);
      if (!Number.isFinite(created) || now - created > QUARANTINE_MAX_AGE_MS) return false;
    }
    return !me || x.owner === null || personOf(x.owner) === me;
  });
  if (keep.length !== items.length) writeQueue(keep);
  return items.length - keep.length;
}

/**
 * Posao 23-7 (odluka 13): globalni administrator nema offline red. Postojeće stavke tog
 * korisnika (iz ranijih verzija, bilo koje firme) uklanjaju se bez slanja: ponavljanje bi
 * otišlo u trenutno odabranu firmu, a ne u onu u kojoj je zahtjev nastao. Vraća broj stavki.
 */
export function discardGlobalAdminWakeQueueItems(userId: string): number {
  const items = readQueue();
  const keep = items.filter(x => !x.owner || x.owner.split(':')[1] !== userId);
  if (keep.length !== items.length) writeQueue(keep);
  return items.length - keep.length;
}

/**
 * Stavke vidljive trenutnoj sesiji: njene vlastite, iste osobe iz ranije verzije
 * prava (samo za pregled/odbacivanje) i naslijeđene bez vlasnika.
 */
export function getWakeQueueItems(owner: string | null = scopeSnapshot().scope): QueuedItem[] {
  return readQueue().filter(x => x.owner === null || (owner !== null && personOf(x.owner) === personOf(owner)));
}

export function getWakeQueueLength(): number {
  const owner = scopeSnapshot().scope;
  return readQueue().filter(x => x.status === 'pending' && owner !== null && x.owner === owner).length;
}

export function isSleepOrColdStartError(error: unknown): boolean {
  const e = error as AxiosError;
  if (e.code === 'ERR_NETWORK' || e.message === 'Network Error') return true;
  if (e.code === 'ECONNABORTED') return true;
  const s = e.response?.status;
  if (s === 502 || s === 503 || s === 504 || s === 408 || s === 524) return true;
  return false;
}

function normalizeRequestUrl(config: InternalAxiosRequestConfig): string {
  let u = config.url || '';
  if (u.startsWith('http')) {
    try {
      const parsed = new URL(u);
      u = parsed.pathname + (parsed.search || '');
    } catch {
      return '/';
    }
  }
  if (!u.startsWith('/')) u = `/${u}`;
  return u;
}

function serializeBody(data: unknown): { ok: boolean; value: unknown } {
  if (data == null || data === '') return { ok: true, value: undefined };
  if (typeof data === 'string') {
    if (data.length > MAX_JSON_CHARS) return { ok: false, value: undefined };
    try {
      return { ok: true, value: JSON.parse(data) };
    } catch {
      return { ok: false, value: undefined };
    }
  }
  if (typeof data === 'object') {
    const s = JSON.stringify(data);
    if (s.length > MAX_JSON_CHARS) return { ok: false, value: undefined };
    return { ok: true, value: JSON.parse(s) };
  }
  return { ok: false, value: undefined };
}

function stableKey(owner: string | null, method: string, url: string, data: unknown): string {
  return `${owner}|${method}|${url}|${JSON.stringify(data ?? null)}`;
}

function shouldEnqueue(config: InternalAxiosRequestConfig | undefined, error: unknown): boolean {
  if (!config || config.skipWakeQueue || isGlobalAdminSession()) return false;
  const path = normalizeRequestUrl(config);
  if (isSensitiveRequest(path, config.data)) return false;
  if (!isSleepOrColdStartError(error)) return false;
  // Time events have a dedicated account-scoped queue with original event times.
  if (isTimeMutation(path)) return false;
  if (config.data instanceof FormData) return false;
  // Samo operacije iz kataloga sigurnih ponavljanja.
  return Boolean(matchWakeOperation(config.method || 'get', path));
}

export function enqueueFromAxiosError(error: AxiosError): void {
  const config = error.config as InternalAxiosRequestConfig | undefined;
  if (!shouldEnqueue(config, error)) return;
  // Zahtjev mora pripadati sesiji koja je i sada aktivna (SEC-02).
  const session = config!.zomaSession;
  if (!session?.scope || !isSnapshotCurrent(session)) return;

  const body = serializeBody(config!.data);
  if (!body.ok) return;
  const method = (config!.method || 'post').toUpperCase();
  const url = normalizeRequestUrl(config!);
  const op = matchWakeOperation(method, url)!;
  const key = stableKey(session.scope, method, url, body.value);

  const item: QueuedItem = {
    id: newId(),
    method,
    url,
    data: body.value,
    createdAt: new Date().toISOString(),
    owner: session.scope,
    operation: op.key,
    status: 'pending',
    attempts: 0,
    idempotencyKey: readIdempotencyHeader(config) || newIdempotencyKey(),
  };

  const q = readQueue().filter(x => x.status !== 'pending' || stableKey(x.owner, x.method, x.url, x.data) !== key);
  // Pun red (po vlasniku): ništa se tiho ne briše; novi zahtjev ostaje obična greška koju korisnik vidi.
  if (q.filter(x => x.owner === session.scope).length >= MAX_ITEMS) return;
  q.push(item);
  writeQueue(q);

  window.dispatchEvent(new CustomEvent('zoma-api-wake-queued', { detail: { length: getWakeQueueLength() } }));
}

export type FlushResult = { sent: number; remaining: number };

function remainingCount(): number {
  return readQueue().length + getLegacyTimeQueueErrors().length;
}

function serverReason(err: unknown): string {
  const ax = err as AxiosError<{ message?: unknown }>;
  const status = ax.response?.status;
  const msg = ax.response?.data && typeof ax.response.data.message === 'string' ? ax.response.data.message.slice(0, 200) : '';
  if (status === 409) return msg || 'Konflikt: podatak je u međuvremenu promijenjen. Provjerite ga prije ponovnog slanja.';
  if (status && status >= 500) return msg || `Greška poslužitelja (${status}). Nije sigurno je li promjena upisana; provjerite podatak prije ponovnog slanja.`;
  return msg || (status ? `Zahtjev odbijen (${status}).` : 'Zahtjev nije uspio.');
}

async function runFlush(api: AxiosInstance): Promise<FlushResult> {
  migrateLegacyWakeQueue();
  const session = scopeSnapshot();
  const owner = session.scope;
  if (!owner) return { sent: 0, remaining: remainingCount() };
  // Ista osoba, promijenjena prava: stavke se ne šalju, nego idu u karantenu za ručni pregled.
  for (const x of readQueue()) {
    if (x.status === 'pending' && x.owner && x.owner !== owner && personOf(x.owner) === personOf(owner)) {
      updateItem(x.id, { status: 'quarantined', reason: 'Vaša prava su se promijenila nakon spremanja; zahtjev se neće automatski poslati.' });
    }
  }
  const ids = readQueue().filter(x => x.status === 'pending' && x.owner === owner).map(x => x.id);
  if (!ids.length) return { sent: 0, remaining: remainingCount() };

  try {
    await api.get('/health', { timeout: 28000, skipWakeQueue: true });
  } catch {
    return { sent: 0, remaining: remainingCount() };
  }

  let sent = 0;
  for (const id of ids) {
    if (!isSnapshotCurrent(session)) break;
    const item = readQueue().find(x => x.id === id);
    if (!item || item.status !== 'pending' || item.owner !== owner) continue;
    if (isSensitiveRequest(item.url, item.data)) {
      updateItem(id, null);
      continue;
    }
    const op = matchWakeOperation(item.method, item.url);
    if (!op || op.key !== item.operation) {
      updateItem(id, { status: 'quarantined', reason: 'Operacija nije u katalogu sigurnih ponavljanja.' });
      continue;
    }
    // Ključ se sprema prije slanja: osvježavanje ili druga kartica šalju isti ključ.
    const idempotencyKey = ensureItemKey(item);
    updateItem(id, { attempts: (item.attempts || 0) + 1, lastAttemptAt: new Date().toISOString(), idempotencyKey });
    try {
      await api.request({
        method: item.method,
        url: item.url,
        data: item.data,
        headers: { [IDEMPOTENCY_HEADER]: idempotencyKey },
        skipWakeQueue: true,
        zomaSession: session,
        zomaStrictSession: true,
      });
      updateItem(id, null);
      sent++;
    } catch (err) {
      if (err instanceof ScopeChangedError || !isSnapshotCurrent(session)) break;
      const ax = err as AxiosError;
      const status = ax.response?.status;
      if (status === 401 || isSleepOrColdStartError(err)) break;
      if (status === 409 && (ax.response?.data as { code?: unknown } | undefined)?.code === 'idempotency_in_progress') {
        // Isti zahtjev se još obrađuje na serveru: ostaje na čekanju, šalje se pri sljedećem pražnjenju.
        continue;
      }
      if (status === 404 && item.method === 'DELETE') {
        // Zapis je već obrisan: željeno stanje je postignuto.
        updateItem(id, null);
        sent++;
        continue;
      }
      // REL-01: trajni odgovor (4xx/5xx) ne briše stavku; ide u dead-letter za ručnu odluku.
      updateItem(id, { status: 'failed', httpStatus: status, reason: serverReason(err) });
    }
  }

  if (sent > 0) {
    window.dispatchEvent(new CustomEvent('zoma-wake-queue-flushed', { detail: { sent } }));
  }
  return { sent, remaining: remainingCount() };
}

let activeFlush: Promise<FlushResult> | null = null;

type LockManager = { request: (name: string, opts: { ifAvailable: boolean }, cb: (lock: unknown) => Promise<FlushResult>) => Promise<FlushResult> };

/**
 * Pinga health, zatim redom ponavlja stavke trenutne sesije. Zaustavlja se ako opet
 * dobije sleep grešku ili ako se sesija promijeni (provjera prije slanja i nakon
 * svakog asinkronog koraka). Istodobno smije postojati samo jedno pražnjenje:
 * unutar kartice dijeli se isti Promise, između kartica Web Locks (ako postoji).
 */
export function flushApiWakeQueue(api: AxiosInstance): Promise<FlushResult> {
  // Posao 23-7 (odluka 13): globalni administrator nema offline red.
  if (isGlobalAdminSession()) return Promise.resolve({ sent: 0, remaining: 0 });
  if (activeFlush) return activeFlush;
  const locks = (typeof navigator !== 'undefined' ? (navigator as unknown as { locks?: LockManager }).locks : undefined);
  const run = locks
    ? locks.request('zoma-api-wake-queue', { ifAvailable: true }, lock => (lock ? runFlush(api) : Promise.resolve({ sent: 0, remaining: remainingCount() })))
    : runFlush(api);
  activeFlush = run.finally(() => { activeFlush = null; });
  return activeFlush;
}

/** Ručno: vrati neuspjelu stavku trenutne sesije u red i pokušaj odmah. */
export function retryWakeQueueItem(api: AxiosInstance, id: string): Promise<FlushResult> | null {
  const owner = scopeSnapshot().scope;
  const item = readQueue().find(x => x.id === id);
  if (!owner || !item || item.owner !== owner || item.status !== 'failed') return null;
  if (!matchWakeOperation(item.method, item.url) || isSensitiveRequest(item.url, item.data)) return null;
  updateItem(id, { status: 'pending', reason: undefined, httpStatus: undefined });
  // Ako pražnjenje upravo traje, ono ne zna za ovu stavku: pokreni novo nakon njega.
  return (activeFlush || Promise.resolve()).then(() => flushApiWakeQueue(api));
}

/** Ručno: odbaci stavku trenutne sesije ili naslijeđenu stavku bez vlasnika. */
export function discardWakeQueueItem(id: string): boolean {
  const owner = scopeSnapshot().scope;
  const item = readQueue().find(x => x.id === id);
  if (!owner || !item || (item.owner !== null && personOf(item.owner) !== personOf(owner))) return false;
  updateItem(id, null);
  return true;
}

// Pri učitavanju aplikacije odmah ukloni naslijeđene osjetljive stavke (SEC-03) i staru karantenu.
try { migrateLegacyWakeQueue(); purgeForeignAndStaleWakeQueueItems(null); } catch { /* localStorage nedostupan */ }
